/**
 * Element layout for a pull-quote template's "Set as Graphic Standard".
 *
 * A pull-quote card fills its canvas, so the per-aspect placement config (the
 * whole composition's position/scale, what Creator Studio captures) has nothing
 * to say about a logo or headshot the user moved INSIDE the card. That geometry
 * lives only in the template's `source`. Until PLAT-11977 the save wrote the
 * card's whole live source as the template source — one card's text, colours,
 * canvas size and aspect-fitted background all became "the template". These
 * helpers do for geometry what `applyControlOverrides` does for controls: the
 * template keeps everything it had, and only the elements whose position or
 * size differs from the saved standard take the live values, each part behind
 * the modal's Position / Scale opt-in.
 *
 * Elements are matched by index path, the same addressing `elementPaths` use
 * everywhere else. Elements bound to a BACKGROUND quote control are skipped:
 * their size is fitted to the card's aspect ratio at load time
 * (`adjustBackgroundImageAspectRatio`), so it always differs and is never the
 * user's change.
 */

import { QuoteControlType } from '@src/components/sidepanel/CompositionSettings/types';

import type {
  AnyElement,
  ControlConfiguration,
  Source,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { PlacementParts } from '@src/components/sidepanel/CompositionSettings/UpdateStandardsModal';

const POSITION_KEYS = ['x', 'y'] as const;
const SCALE_KEYS = ['width', 'height'] as const;

type GeometryKey = (typeof POSITION_KEYS)[number] | (typeof SCALE_KEYS)[number];

export interface ElementLayoutChange {
  /** Index path from the template root, as `elementPaths` address elements. */
  path: number[];
  name?: string;
  position: boolean;
  scale: boolean;
}

/** An element's geometry as the source states it (raw values, e.g. "83.6%"). */
export interface ElementGeometry {
  x?: unknown;
  y?: unknown;
  width?: unknown;
  height?: unknown;
}

/**
 * One element's position and size in the template (before) and on the card
 * (after), for the modal's Position / Size rows (PLAT-11993). `after` falls
 * back to the template's value wherever the renderer does not report one.
 */
export interface ElementLayoutReadout {
  path: number[];
  name?: string;
  before: ElementGeometry;
  after: ElementGeometry;
  positionChanged: boolean;
  sizeChanged: boolean;
}

/** Per-element opt-in for the moved geometry, keyed by `pathKey(path)`. A
 *  path that is absent is included (the modal checks changes by default). */
export type ElementPlacementParts = Record<
  string,
  { position: boolean; size: boolean }
>;

type ElementLike = { elements?: unknown[]; name?: unknown } & Record<
  string,
  unknown
>;

const isElementLike = (v: unknown): v is ElementLike =>
  !!v && typeof v === 'object';

const same = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
};

// A geometry value the renderer does not report (a full-bleed background's
// `y`, say) is unknown, not "moved": the same rule as for control values.
const differsOn = (
  keys: readonly GeometryKey[],
  template: ElementLike,
  live: ElementLike,
): boolean => keys.some(k => live[k] != null && !same(template[k], live[k]));

export const pathKey = (path: number[]) => path.join('.');

const geometryOf = (el: ElementLike): ElementGeometry => ({
  x: el.x,
  y: el.y,
  width: el.width,
  height: el.height,
});

/** Paths of elements a BACKGROUND quote control binds — never captured. */
export function backgroundElementPaths(
  controls: ControlConfiguration[] | undefined,
): Set<string> {
  const out = new Set<string>();
  for (const c of controls ?? []) {
    if (c.default?.quote !== QuoteControlType.BACKGROUND) continue;
    for (const p of c.elementPaths ?? []) out.add(pathKey(p));
  }
  return out;
}

const walk = (
  template: ElementLike | undefined,
  live: ElementLike | undefined,
  path: number[],
  visit: (t: ElementLike, l: ElementLike, path: number[]) => void,
) => {
  const tElsRaw = template?.elements;
  const lElsRaw = live?.elements;
  const tEls = Array.isArray(tElsRaw) ? tElsRaw : [];
  const lEls = Array.isArray(lElsRaw) ? lElsRaw : [];
  tEls.forEach((t, i) => {
    const l = lEls[i];
    if (!isElementLike(t) || !isElementLike(l)) return;
    const p = [...path, i];
    visit(t, l, p);
    walk(t, l, p, visit);
  });
};

/**
 * Every element's position and size, template vs card, by index path. The
 * element a BACKGROUND control binds is left out (its size is aspect-fitted
 * at load). Elements the card doesn't have are left out too.
 */
export function readElementLayout(
  templateSource: Source | null | undefined,
  liveSource: Source | null | undefined,
  controls?: ControlConfiguration[],
): ElementLayoutReadout[] {
  const skip = backgroundElementPaths(controls);
  const out: ElementLayoutReadout[] = [];
  walk(
    templateSource ?? undefined,
    liveSource ?? undefined,
    [],
    (t, l, path) => {
      if (skip.has(pathKey(path))) return;
      const before = geometryOf(t);
      const live = geometryOf(l);
      const after: ElementGeometry = {
        x: live.x ?? before.x,
        y: live.y ?? before.y,
        width: live.width ?? before.width,
        height: live.height ?? before.height,
      };
      out.push({
        path,
        name: typeof t.name === 'string' ? t.name : undefined,
        before,
        after,
        positionChanged: differsOn(POSITION_KEYS, t, l),
        sizeChanged: differsOn(SCALE_KEYS, t, l),
      });
    },
  );
  return out;
}

/**
 * Which elements' position and/or size differ between the template's saved
 * source and the live card source. Empty when nothing moved.
 */
export function diffElementLayout(
  templateSource: Source | null | undefined,
  liveSource: Source | null | undefined,
  controls?: ControlConfiguration[],
): ElementLayoutChange[] {
  return readElementLayout(templateSource, liveSource, controls)
    .filter(r => r.positionChanged || r.sizeChanged)
    .map(r => ({
      path: r.path,
      name: r.name,
      position: r.positionChanged,
      scale: r.sizeChanged,
    }));
}

const isUniformParts = (
  parts: PlacementParts | ElementPlacementParts,
): parts is PlacementParts =>
  typeof (parts as PlacementParts).position === 'boolean' &&
  typeof (parts as PlacementParts).scale === 'boolean';

/**
 * The template source to save: the template's own source with, for each
 * element that moved or resized, the live x/y and width/height — each part
 * only when opted in. `parts` is either one choice for every element
 * (`{ position, scale }`) or a choice per element path (PLAT-11993), where an
 * element that isn't listed is included. Everything else — canvas size, text,
 * colours, fonts, elements that did not move — is the template's. Returns the
 * input template source itself when there is nothing to merge.
 */
export function mergeElementLayout(
  templateSource: Source,
  liveSource: Source | null | undefined,
  parts: PlacementParts | ElementPlacementParts,
  controls?: ControlConfiguration[],
): Source {
  const partsFor = (path: number[]) => {
    if (isUniformParts(parts)) {
      return { position: parts.position, size: parts.scale };
    }
    return parts[pathKey(path)] ?? { position: true, size: true };
  };
  const changes = diffElementLayout(templateSource, liveSource, controls);
  const wanted = changes.filter(c => {
    const p = partsFor(c.path);
    return (p.position && c.position) || (p.size && c.scale);
  });
  if (wanted.length === 0) return templateSource;

  const byPath = new Map(wanted.map(c => [pathKey(c.path), c]));
  const clone = JSON.parse(JSON.stringify(templateSource)) as Source;

  walk(clone, liveSource ?? undefined, [], (t, l, path) => {
    const change = byPath.get(pathKey(path));
    if (!change) return;
    const p = partsFor(path);
    const keys: GeometryKey[] = [
      ...(p.position && change.position ? POSITION_KEYS : []),
      ...(p.size && change.scale ? SCALE_KEYS : []),
    ];
    for (const k of keys) {
      if (l[k] === undefined) continue;
      (t as Record<string, unknown>)[k] = l[k];
    }
  });
  return clone;
}

/**
 * The live card source as the panel sees it: the renderer wraps the template
 * composition in one outer composition element, so the template's elements are
 * `elements[0].elements`. Returns undefined when the renderer has nothing yet.
 */
export function unwrapLiveCardSource(
  rendererSource: Record<string, unknown> | null | undefined,
): Source | undefined {
  if (!rendererSource) return undefined;
  const outer: unknown = (
    rendererSource.elements as unknown[] | undefined
  )?.[0];
  if (!isElementLike(outer)) return undefined;
  const inner = Array.isArray(outer.elements) ? outer.elements : [];
  return { ...rendererSource, elements: inner as AnyElement[] } as Source;
}
